import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Pager } from "@/components/admin/Pager";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useLeads } from "@/hooks/useLeads";
import {
  resetLeadWorkflow,
  upsertLeadState,
  type LeadRow,
} from "@/lib/admin/leads.functions";
import {
  LEAD_SOURCE_OPTIONS,
  LEAD_STATUS_OPTIONS,
  type LeadSource,
  type LeadStatus,
} from "@/lib/admin/leads.schemas";

export const Route = createFileRoute("/_admin/admin/leads")({
  component: LeadsPage,
});

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function sourceLabel(s: LeadSource): string {
  return LEAD_SOURCE_OPTIONS.find((o) => o.value === s)?.label ?? s;
}

function statusLabel(s: LeadStatus): string {
  return LEAD_STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;
}

function statusVariant(
  s: LeadStatus,
): "default" | "secondary" | "outline" | "destructive" {
  switch (s) {
    case "converted":
      return "default";
    case "interested":
    case "contacted":
      return "secondary";
    case "spam":
      return "destructive";
    default:
      return "outline";
  }
}

function LeadsPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<LeadStatus | "all">("all");
  const [source, setSource] = useState<LeadSource | "all">("all");
  const [selected, setSelected] = useState<LeadRow | null>(null);

  const query = useLeads({
    page,
    search,
    status: status === "all" ? undefined : status,
    source: source === "all" ? undefined : source,
  });

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div>
      <AdminPageHeader
        title="Leads"
        description="Every enquiry across Contact, Delivery Requests and Newsletter — with status and internal notes."
      />

      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <form
          className="flex w-full max-w-md gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            applySearch();
          }}
        >
          <Input
            placeholder="Search name, email, phone, message…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button type="submit" variant="outline" size="sm">
            Search
          </Button>
          {search ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput("");
                setSearch("");
                setPage(1);
              }}
            >
              Clear
            </Button>
          ) : null}
        </form>
        <div className="flex flex-wrap gap-2">
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v as LeadStatus | "all");
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LEAD_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={source}
            onValueChange={(v) => {
              setPage(1);
              setSource(v as LeadSource | "all");
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {LEAD_SOURCE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {query.isError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            Couldn't load leads. Please refresh and try again.
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : query.data && query.data.rows.length > 0 ? (
        <>
          <div className="hidden overflow-hidden rounded-md border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.rows.map((r) => (
                  <TableRow
                    key={`${r.source}:${r.source_id}`}
                    className="cursor-pointer"
                    onClick={() => setSelected(r)}
                  >
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDate(r.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{sourceLabel(r.source)}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium text-foreground">
                        {r.name || "—"}
                      </div>
                      <div className="text-muted-foreground">
                        {r.email || "—"}
                      </div>
                      {r.phone ? (
                        <div className="text-muted-foreground">{r.phone}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="max-w-md truncate text-sm text-muted-foreground">
                      {r.subject || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(r.lead_status)}>
                        {statusLabel(r.lead_status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(r);
                        }}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ul className="space-y-3 md:hidden">
            {query.data.rows.map((r) => (
              <li
                key={`${r.source}:${r.source_id}`}
                className="cursor-pointer rounded-md border p-3"
                onClick={() => setSelected(r)}
              >
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatDate(r.created_at)}</span>
                  <Badge variant={statusVariant(r.lead_status)}>
                    {statusLabel(r.lead_status)}
                  </Badge>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant="outline">{sourceLabel(r.source)}</Badge>
                  <span className="font-medium text-foreground">
                    {r.name || r.email || "—"}
                  </span>
                </div>
                {r.email ? (
                  <div className="text-sm text-muted-foreground">{r.email}</div>
                ) : null}
                {r.subject ? (
                  <p className="mt-2 line-clamp-2 whitespace-pre-wrap text-sm text-foreground">
                    {r.subject}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>

          <Pager
            page={query.data.page}
            pageSize={query.data.pageSize}
            total={query.data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No leads match these filters.
        </p>
      )}

      <LeadDrawer lead={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function LeadDrawer({
  lead,
  onClose,
}: {
  lead: LeadRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const upsertFn = useServerFn(upsertLeadState);
  const resetFn = useServerFn(resetLeadWorkflow);

  const initial = useMemo(
    () => ({
      status: (lead?.lead_status ?? "new") as LeadStatus,
      notes: lead?.lead_notes ?? "",
    }),
    [lead?.source, lead?.source_id, lead?.lead_status, lead?.lead_notes],
  );

  const [status, setStatus] = useState<LeadStatus>(initial.status);
  const [notes, setNotes] = useState<string>(initial.notes);

  // Reset local editor state whenever a new lead is opened
  const editorKey = `${lead?.source}:${lead?.source_id}`;
  const [lastKey, setLastKey] = useState<string>(editorKey);
  if (editorKey !== lastKey) {
    setLastKey(editorKey);
    setStatus(initial.status);
    setNotes(initial.notes);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!lead) return;
      await upsertFn({
        data: {
          source: lead.source,
          sourceId: lead.source_id,
          status,
          notes,
        },
      });
    },
    onSuccess: () => {
      toast.success("Lead updated");
      queryClient.invalidateQueries({ queryKey: ["admin", "leads"] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "update_failed";
      toast.error(
        msg === "lead_not_found"
          ? "The source record no longer exists."
          : "Couldn't save changes. Please try again.",
      );
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      if (!lead) return;
      await resetFn({
        data: { source: lead.source, sourceId: lead.source_id },
      });
    },
    onSuccess: () => {
      toast.success("Workflow reset");
      queryClient.invalidateQueries({ queryKey: ["admin", "leads"] });
      onClose();
    },
    onError: () => toast.error("Couldn't reset workflow."),
  });

  return (
    <Sheet open={lead !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        {lead ? (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Badge variant="outline">{sourceLabel(lead.source)}</Badge>
                <span>{lead.name || lead.email || "Lead"}</span>
              </SheetTitle>
              <SheetDescription>
                Received {formatDate(lead.created_at)}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-4 text-sm">
              <section className="space-y-1">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Contact
                </h3>
                <div>Email: {lead.email || "—"}</div>
                <div>Phone: {lead.phone || "—"}</div>
                {lead.origin ? <div>Origin: {lead.origin}</div> : null}
              </section>

              <section className="space-y-1">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Message
                </h3>
                <p className="whitespace-pre-wrap rounded-md border bg-muted/40 p-3">
                  {lead.subject || "—"}
                </p>
              </section>

              <section className="space-y-2">
                <label
                  htmlFor="lead-status"
                  className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  Status
                </label>
                <Select
                  value={status}
                  onValueChange={(v) => setStatus(v as LeadStatus)}
                >
                  <SelectTrigger id="lead-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </section>

              <section className="space-y-2">
                <label
                  htmlFor="lead-notes"
                  className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  Internal notes
                </label>
                <Textarea
                  id="lead-notes"
                  rows={5}
                  maxLength={5000}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Only visible to admins."
                />
                <p className="text-xs text-muted-foreground">
                  {notes.length}/5000
                </p>
              </section>

              {lead.lead_updated_at ? (
                <p className="text-xs text-muted-foreground">
                  Last updated {formatDate(lead.lead_updated_at)}
                </p>
              ) : null}
            </div>

            <SheetFooter className="mt-6 flex-col gap-2 sm:flex-row sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (window.confirm("Reset status to New and clear notes?")) {
                    resetMutation.mutate();
                  }
                }}
                disabled={resetMutation.isPending || saveMutation.isPending}
              >
                Reset workflow state
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={saveMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
            </SheetFooter>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}